import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Services.module.css';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import ChatBot from '../components/ChatBot';

const Services = () => {
    const services = [
        {
            icon: '💻',
            title: 'Web Development',
            description: 'Custom websites and web applications built with modern technologies and best practices.',
            features: [
                'Responsive Design',
                'Performance Optimisation',
                'SEO Friendly',
                'Cross-browser Compatibility',
                'Progressive Web Apps',
                'API Integration'
            ],
            benefits: [
                'Increased online presence',
                'Improved user engagement',
                'Higher conversion rates'
            ]
        },
        {
            icon: '🎨',
            title: 'UI/UX Design',
            description: 'Beautiful and intuitive user interfaces that provide exceptional user experiences.',
            features: [
                'User Research & Personas',
                'Wireframing & Prototyping',
                'Visual Design Systems',
                'Usability Testing',
                'Interaction Design',
                'Design System Development'
            ],
            benefits: [
                'Enhanced user satisfaction',
                'Reduced bounce rates',
                'Increased customer loyalty'
            ]
        },
        {
            icon: '📱',
            title: 'Mobile Development',
            description: 'Native and cross-platform mobile applications for iOS and Android devices.',
            features: [
                'Native iOS & Android Development',
                'Cross-platform Solutions (React Native/Flutter)',
                'App Store Deployment',
                'Performance Optimisation',
                'Push Notifications',
                'Offline Functionality'
            ],
            benefits: [
                'Mobile market reach',
                'Improved customer accessibility',
                'Enhanced brand presence'
            ]
        },
        {
            icon: '🔍',
            title: 'SEO Optimisation',
            description: 'Improve your website visibility and ranking in search engine results.',
            features: [
                'Comprehensive Keyword Research',
                'On-page Technical SEO',
                'Content Strategy Development',
                'Analytics & Performance Tracking',
                'Local SEO Optimisation',
                'Competitor Analysis'
            ],
            benefits: [
                'Higher organic traffic',
                'Improved search rankings',
                'Better ROI on marketing'
            ]
        },
        {
            icon: '📊',
            title: 'Digital Marketing',
            description: 'Comprehensive digital marketing strategies to grow your online presence.',
            features: [
                'Social Media Strategy',
                'Content Marketing',
                'Email Campaign Management',
                'Performance Analytics & Reporting',
                'Conversion Rate Optimisation',
                'Growth Strategy Development'
            ],
            benefits: [
                'Increased brand awareness',
                'Higher lead generation',
                'Improved customer acquisition'
            ]
        },
        {
            icon: '☁️',
            title: 'Cloud Solutions',
            description: 'Scalable cloud infrastructure and deployment solutions for your applications.',
            features: [
                'Cloud Architecture Design',
                'DevOps & CI/CD Pipelines',
                'Serverless Solutions',
                'Security & Compliance',
                'Database Management',
                'Performance Monitoring'
            ],
            benefits: [
                'Scalable infrastructure',
                'Reduced operational costs',
                'Improved reliability'
            ]
        }
    ];

    const processSteps = [
        {
            number: '01',
            title: 'Discovery & Consultation',
            description: 'We begin with an in-depth analysis of your business objectives, target audience, and technical requirements to create a tailored strategy.'
        },
        {
            number: '02',
            title: 'Strategic Planning',
            description: 'Our team develops a comprehensive project roadmap with clear milestones, deliverables, and success metrics aligned with your goals.'
        },
        {
            number: '03',
            title: 'Design & Prototyping',
            description: 'We create intuitive user experiences and visually appealing designs through wireframes, mockups, and interactive prototypes.'
        },
        {
            number: '04',
            title: 'Development & Implementation',
            description: 'Our developers use cutting-edge technologies to build robust, scalable solutions following industry best practices and coding standards.'
        },
        {
            number: '05',
            title: 'Quality Assurance',
            description: 'Rigorous testing across multiple devices and scenarios ensures flawless performance, security, and user experience.'
        },
        {
            number: '06',
            title: 'Launch & Ongoing Support',
            description: 'We handle seamless deployment and provide continuous maintenance, updates, and optimisation for long-term success.'
        }
    ];

    const valueProps = [
        {
            icon: '⏱️',
            title: 'Timely Delivery',
            description: 'We pride ourselves on meeting deadlines without compromising quality.'
        },
        {
            icon: '🛡️',
            title: 'Quality Assurance',
            description: 'Rigorous testing processes ensure your project meets the highest standards.'
        },
        {
            icon: '🔧',
            title: 'Ongoing Support',
            description: 'Continuous maintenance and updates to keep your solution performing optimally.'
        },
        {
            icon: '📈',
            title: 'Results-Driven',
            description: 'We focus on delivering measurable outcomes that drive your business forward.'
        }
    ];

    return (
        <div className={styles.servicesPage}>
            {/* Hero Section */}
            <section className={styles.hero}>
                <div className={styles.container}>
                    <h1>Our Digital Solutions</h1>
                    <p className={styles.subtitle}>
                        Comprehensive services designed to elevate your digital presence and drive business growth through innovative technology solutions.
                    </p>
                </div>
            </section>

            {/* Value Proposition Section */}
            <section className={styles.valueSection}>
                <div className={styles.container}>
                    <h2>Why Choose Apexbit?</h2>
                    <div className={styles.valueGrid}>
                        {valueProps.map((value, index) => (
                            <Card key={index} variant="default" padding="medium" className={styles.valueCard}>
                                <div className={styles.valueIcon}>{value.icon}</div>
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className={styles.servicesSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeader}>
                        <h2>Our Comprehensive Services</h2>
                        <p>Tailored solutions to meet your unique business needs and objectives</p>
                    </div>
                    <div className={styles.servicesGrid}>
                        {services.map((service, index) => (
                            <Card key={index} variant="elevated" padding="large" className={styles.serviceCard}>
                                <div className={styles.serviceHeader}>
                                    <div className={styles.serviceIcon}>{service.icon}</div>
                                    <h3>{service.title}</h3>
                                </div>
                                <p className={styles.serviceDescription}>{service.description}</p>
                                
                                <div className={styles.featuresSection}>
                                    <h4>What We Deliver</h4>
                                    <ul className={styles.featuresList}>
                                        {service.features.map((feature, featureIndex) => (
                                            <li key={featureIndex}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div className={styles.benefitsSection}>
                                    <h4>Your Benefits</h4>
                                    <ul className={styles.benefitsList}>
                                        {service.benefits.map((benefit, benefitIndex) => (
                                            <li key={benefitIndex}>{benefit}</li>
                                        ))}
                                    </ul>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className={styles.processSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeader}>
                        <h2>Our Collaborative Process</h2>
                        <p>A transparent, step-by-step approach ensuring your vision becomes reality</p>
                    </div>
                    <div className={styles.processSteps}>
                        {processSteps.map((step, index) => (
                            <Card key={index} variant="default" padding="medium" className={styles.processStep}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepNumber}>{step.number}</div>
                                    <h3>{step.title}</h3>
                                </div>
                                <p>{step.description}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className={styles.ctaSection}>
                <div className={styles.container}>
                    <h2>Ready to Transform Your Digital Presence?</h2>
                    <p>Let's discuss your project requirements and create a custom solution that drives results for your business.</p>
                    <div className={styles.ctaButtons}>
                        <Button as={Link} to="/contact" variant="primary" size="large">
                            Start Your Project
                        </Button>
                    </div>
                </div>
            </section>
            <ChatBot/>
        </div>
    );
};

export default Services;
